import {
  REVIEWABLE_DOCUMENT_CATEGORIES,
  ReviewableDocumentCategoryKey,
} from '@bordrio/shared';
import { useMatch } from 'react-router';
import { NifOrderFragment } from '@src/generated/graphql';
import {
  IntakeFlowStep,
  UseStepFlowResult,
  useStepFlow,
} from '@src/hooks/useStepFlow';
import { isNifOrderMinor } from '../utils/age';

export const NIF_SIDEBAR_STEP_CATEGORIES = [
  // i18n.t('nif:sidebar.prepare_documents', 'Prepare documents')
  'nif:sidebar.prepare_documents',
  // i18n.t('nif:sidebar.personal_details', 'Personal details')
  'nif:sidebar.personal_details',
  // i18n.t('nif:sidebar.residence', 'Residence')
  'nif:sidebar.residence',
  // i18n.t('nif:sidebar.review', 'Review')
  'nif:sidebar.review',
] as const;

type NifSidebarStepCategory = (typeof NIF_SIDEBAR_STEP_CATEGORIES)[number];

function isBornInPortugal(order: NifOrderFragment): boolean {
  return order.birthCountry === 'PT';
}

function hasDocumentType(
  order: NifOrderFragment,
  documentCategoryKey: ReviewableDocumentCategoryKey,
): boolean {
  return order.intakeDocuments.some(
    (d) =>
      REVIEWABLE_DOCUMENT_CATEGORIES[documentCategoryKey].id ===
      d.documentCategoryId,
  );
}

const NIF_STEPS: IntakeFlowStep<NifSidebarStepCategory, NifOrderFragment>[] = [
  {
    key: 'prepare-documents',
    category: 'nif:sidebar.prepare_documents',
  },
  {
    key: 'personal-details',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) => !!order.givenNames,
  },
  {
    key: 'proof-of-identity',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) => hasDocumentType(order, 'nif_proof_of_identity'),
  },
  // have to check for prooof of identity in all following steps since it can be set to null
  // if the document type is changed
  {
    key: 'minor-birth-certificate',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) =>
      hasDocumentType(order, 'nif_minor_birth_certificate'),
    shouldIncludeInFlow: (order) => isNifOrderMinor(order),
  },
  {
    key: 'is-portuguese',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) => order.isPortugueseCitizen != null,
    shouldIncludeInFlow: (order) =>
      !isNifOrderMinor(order) && !isBornInPortugal(order),
  },
  {
    key: 'portuguese-birth-certificate',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) =>
      hasDocumentType(order, 'nif_portuguese_birth_certificate'),
    shouldIncludeInFlow: (order) =>
      !isNifOrderMinor(order) &&
      (isBornInPortugal(order) || !!order.isPortugueseCitizen),
  },
  {
    key: 'parents-information',
    category: 'nif:sidebar.personal_details',
    isStepCompleted: (order) => !!order.parents.length,
    shouldIncludeInFlow: (order) => isNifOrderMinor(order),
  },
  {
    key: 'residential-address',
    category: 'nif:sidebar.residence',
    isStepCompleted: (order) => !!order.address,
  },
  {
    key: 'proof-of-address',
    category: 'nif:sidebar.residence',
    isStepCompleted: (order) => hasDocumentType(order, 'nif_proof_of_address'),
  },
  {
    key: 'review',
    category: 'nif:sidebar.review',
  },
];

type UseNifOrderOptions =
  | { isNew: true; nifOrder?: undefined }
  | { isNew: false; nifOrder?: NifOrderFragment };

export function useNifOrderFlow({
  isNew,
  nifOrder,
}: UseNifOrderOptions): UseStepFlowResult<NifOrderFragment> {
  const routeMatch = useMatch(
    `/orders/nif/${isNew ? 'new' : 'edit/:id'}/:step`,
  );

  return useStepFlow({
    stepCategories: NIF_SIDEBAR_STEP_CATEGORIES,
    currentStepKey: routeMatch?.params.step,
    isNew,
    item: nifOrder,
    steps: NIF_STEPS,
    makeUrlToStep(stepKey, item) {
      return `/orders/nif/${item ? `edit/${item.id}` : 'new'}/${stepKey}`;
    },
  });
}
